import {
    CustomRoutes,
    mergeTranslations,
    //Resource,
    useStore, //uus
    StoreContextProvider, //uus
    localStorageStore, //uus
} from 'react-admin';
import { Resource } from '@react-admin/ra-rbac';
import { Admin, buildI18nProvider } from '@react-admin/ra-enterprise';
import {
    raTreeLanguageEnglish,
    raTreeLanguageFrench,
} from '@react-admin/ra-tree';
import {
    raTourLanguageEnglish,
    raTourLanguageFrench,
} from '@react-admin/ra-tour';
import { Route } from 'react-router-dom';

import authProvider from './authProvider';
import { Login, Layout } from './layout';
import { Dashboard } from './dashboard';
import englishMessages from './i18n/en';
import frenchMessages from './i18n/fr';
import finnishMessages from './i18n/fi';
import dataProvider from './dataProvider';
import { QueryClient } from '@tanstack/react-query';
import Configuration from './configuration/Configuration';
import SchoolMapPage from './components/SchoolMapPage';
import ActiveUsersPage from './components/ActiveUsersPage';
import NewUsersPage from './components/NewUsersPage';
import LoginStatsPage from './components/LoginStatsPage';
import DataConfirmationsPage from './components/DataConfirmationsPage';
import LegacyMergeRedirect from './resources/user/LegacyMergeRedirect';

import product from './resources/product';
import subscription from './resources/subscription';
import school from './resources/school';
import user from './resources/user';
import teacher from './resources/user/teacher';
import pupil from './resources/user/pupil';
import student from './resources/user/student';
import announcement from './resources/announcement';
import campaign from './resources/campaign';
import group from './resources/group';
import valkey from './resources/valkey';
import materialstorage from './resources/materialstorage';
import hallinta from './resources/hallinta';
import secrets from './resources/secrets';
import JwtKeyList from './resources/hallinta/JwtKeyList';
import OAuthClientList from './resources/hallinta/OAuthClientList';
import OAuthClientEdit from './resources/hallinta/OAuthClientEdit';
import OAuthClientCreate from './resources/hallinta/OAuthClientCreate';
import ApiConfigList from './resources/hallinta/ApiConfigList';
import ApiConfigEdit from './resources/hallinta/ApiConfigEdit';
import ApiConfigCreate from './resources/hallinta/ApiConfigCreate';

import { withLifecycleCallbacks } from 'react-admin';
import { productLifecycleCallbacks } from './resources/product';

import { ThemeName, themes } from './themes/themes';

const messages = {
    en: mergeTranslations(
        englishMessages,
        raTreeLanguageEnglish,
        raTourLanguageEnglish
    ),
    fr: mergeTranslations(
        frenchMessages,
        raTreeLanguageFrench,
        raTourLanguageFrench
    ),
    fi: mergeTranslations(
        finnishMessages,
        raTreeLanguageEnglish, //täytyy selvittää löytyykö suomea
        raTourLanguageEnglish
    ),
};
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            /**
             * The time in milliseconds after data is considered stale.
             * If set to `Infinity`, the data will never be considered stale.
             */
            staleTime: 10000,
            /**
             * If `false`, failed queries will not retry by default.
             * If `true`, failed queries will retry infinitely., failureCount: num
             * If set to an integer number, e.g. 3, failed queries will retry until the failed query count meets that number.
             * If set to a function `(failureCount, error) => boolean` failed queries will retry until the function returns false.
             */
            retry: false,
            /**
             * If set to `true`, the query will refetch on window focus if the data is stale.
             * If set to `false`, the query will not refetch on window focus.
             * If set to `'always'`, the query will always refetch on window focus.
             * If set to a function, the function will be executed with the latest data and query to compute the value.
             * Defaults to `true`.
             */
            refetchOnWindowFocus: false,
        },
    },
});

const i18nProvider = buildI18nProvider(messages, 'fi');

const enhancedDataProvider = withLifecycleCallbacks(dataProvider, [
    productLifecycleCallbacks,
]);

const store = localStorageStore(undefined, 'InfraAdmin'); //uus

const App = () => {
    const [themeName] = useStore<ThemeName>('themeName', 'soft'); //uus
    const lightTheme = themes.find(theme => theme.name === themeName)?.light;
    const darkTheme = themes.find(theme => theme.name === themeName)?.dark;

    return (
        <Admin
            title=""
            store={store} //uus
            dataProvider={enhancedDataProvider}
            authProvider={authProvider}
            dashboard={Dashboard}
            loginPage={Login}
            layout={Layout}
            i18nProvider={i18nProvider}
            disableTelemetry
            lightTheme={lightTheme}
            darkTheme={darkTheme}
            defaultTheme="light"
            queryClient={queryClient}
        >
            <CustomRoutes>
                <Route path="/configuration" element={<Configuration />} />
                <Route path="/schoolmap" element={<SchoolMapPage />} />
                <Route path="/activeusers" element={<ActiveUsersPage />} />
                <Route path="/newusers" element={<NewUsersPage />} />
                <Route path="/loginstats" element={<LoginStatsPage />} />
                <Route
                    path="/dataconfirmations"
                    element={<DataConfirmationsPage />}
                />
                <Route
                    path="/merge-duplicates"
                    element={<LegacyMergeRedirect view="merge" />}
                />
                <Route
                    path="/merge-history"
                    element={<LegacyMergeRedirect view="history" />}
                />
                <Route
                    path="/merge-report"
                    element={<LegacyMergeRedirect view="report" />}
                />
            </CustomRoutes>
            <CustomRoutes noLayout>
                <Route path="/oidc-login/:code" element={<Login />} />
            </CustomRoutes>
            <Resource name="product" {...product} />
            <Resource name="subproduct" />
            <Resource name="subscription" {...subscription} />
            <Resource name="school" {...school} />
            <Resource name="group" {...group} />
            <Resource name="user" {...user} />
            <Resource name="teacher" {...teacher} />
            <Resource name="pupil" {...pupil} />
            <Resource name="student" {...student} />
            <Resource name="announcement" {...announcement} />
            <Resource name="campaign" {...campaign} />
            <Resource name="valkey" {...valkey} />
            <Resource name="materialstorage" {...materialstorage} />
            <Resource name="hallinta" {...hallinta} />
            <Resource name="admin/secrets" {...secrets} />
            <Resource name="admin/jwt-keys" list={JwtKeyList} />
            <Resource
                name="admin/api-configs"
                list={ApiConfigList}
                edit={ApiConfigEdit}
                create={ApiConfigCreate}
            />
            <Resource
                name="admin/oauth-clients"
                list={OAuthClientList}
                edit={OAuthClientEdit}
                create={OAuthClientCreate}
            />
            <Resource name="userlicense" />
            <Resource name="userapplication" />
            <Resource name="usergroup" />
            <Resource name="logevent" />
        </Admin>
    );
};

const AppWrapper = () => (
    <StoreContextProvider value={store}>
        <App />
    </StoreContextProvider>
);

export default AppWrapper;
